import { inject, Injectable } from '@angular/core';
import { PreInitSettingsService } from './settings/pre-init-setting.service';
import { add } from 'date-fns';
import { map, Observable, of, shareReplay, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ADPResourceService } from 'digital-core';
import { ApiPlanResponse, Certificate, Claim, ClaimStatusKeys, Coverage, Equipment, PaymentHistoryItem, PlanDetail, PlanTier } from 'src/app/model/plan/plan-detail';
import { AppConstants } from 'src/app/core/constants';
import { CacheEntry } from 'src/app/model/cache-entry/cache-entry';
import { ISelectedPlan } from './settings';
import { IPlan } from 'src/app/model/portal-profile/IPortalProfile';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class PlanService {
  private preInitSettingsService = inject(PreInitSettingsService);
  private httpClient = inject(HttpClient);
  private storageService = inject(StorageService);
  private resourceService = inject(ADPResourceService);

  private middlewareUrl: string;
  private accountPath: string;
  private readonly DEFAULT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  private goodPlanTierTag = '';
  private betterPlanTierTag = '';
  private bestPlanTierTag = '';
  private listingPlanTierTag = '';
  private cacheDuration: number = this.DEFAULT_CACHE_DURATION;
  private disabledAddonsDealerCodes: string[] = [];
  private mappDealerCodes: string[] = [];

  constructor() {
    this.middlewareUrl = this.preInitSettingsService.getSettings().middleWare!.middlewareUrl;
    this.accountPath = this.preInitSettingsService.getSettings().middleWare!.accountPath;
  }

  initialize() {
    if (this.cacheDuration) {
      return;
    }

    const cacheDurationValue = this.resourceService.getPortalSetting('Global_Site_Cache_Duration')?.Value;
    if (cacheDurationValue) {
      const parsedDuration = parseInt(cacheDurationValue, 10);
      if (!isNaN(parsedDuration) && parsedDuration > 0) {
        this.cacheDuration = parsedDuration * 60 * 1000; // convert minutes to milliseconds
      }
    }
  }

  getPlanDetails(planNumber: string, plan: ISelectedPlan, refresh?: boolean | null): Observable<PlanDetail> {
    this.initialize();
    const cacheKey = `${AppConstants.STORAGE_KEYS.PlanDetail}:${planNumber}`;
    const cacheEntry = this.storageService.getSession<CacheEntry<PlanDetail>>(cacheKey);

    // Check if cache exists, is not being forced to refresh, and is still valid
    if (cacheEntry && refresh !== true && CacheEntry.isCacheValid(cacheEntry.timestamp, this.cacheDuration)) {
      return of(cacheEntry.data);
    }

    const method = AppConstants.API_ENDPOINTS.PlanDetails;

    return this.httpClient.get<ApiPlanResponse[]>(`${this.middlewareUrl}${this.accountPath}${method}?planNumber=${planNumber}&refresh=${refresh ?? false}`).pipe(
      map((res) => {
        try {
          const planDetail = this.buildPlanDetail(res[0], plan);
          if (!planDetail) {
            throw new Error(`Failed to build plan detail for plan number: ${planNumber}`);
          }
          return planDetail;
        } catch (error) {
          throw new Error(`Failed to build plan detail for plan number: ${planNumber}`, { cause: error });
        }
      }),
      tap((planDetail) => {
        if (planDetail) {
          const entry = new CacheEntry(planDetail);
          this.storageService.setSession<CacheEntry<PlanDetail>>(cacheKey, entry);
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  getAllPlanDetails(plans: IPlan[], refresh?: boolean | null): Observable<PlanDetail[]> {
    this.initialize();
    const allCacheKey = `${AppConstants.STORAGE_KEYS.PlanDetail}:all`;
    const cacheEntry = this.storageService.getSession<CacheEntry<PlanDetail[]>>(allCacheKey);

    if (cacheEntry && refresh !== true && CacheEntry.isCacheValid(cacheEntry.timestamp, this.cacheDuration)) {
      return of(cacheEntry.data);
    }

    const method = AppConstants.API_ENDPOINTS.PlanDetails;

    return this.httpClient.get<ApiPlanResponse[]>(`${this.middlewareUrl}${this.accountPath}${method}?refresh=${refresh ?? false}`).pipe(
      map((responses) =>
        responses
          .map((res) => {
            const matchingPlan = plans.find((p) => p.planNumber === res.planNumber) ?? null;
            return this.buildPlanDetail(res, matchingPlan);
          })
          .filter((d): d is PlanDetail => d !== null)
      ),
      tap((planDetails) => {
        if (planDetails.length) {
          this.storageService.setSession<CacheEntry<PlanDetail[]>>(allCacheKey, new CacheEntry(planDetails));
          planDetails.forEach((pd) => {
            this.storageService.setSession<CacheEntry<PlanDetail>>(`${AppConstants.STORAGE_KEYS.PlanDetail}:${pd.planNumber}`, new CacheEntry(pd));
          });
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private buildPlanDetail(raw: ApiPlanResponse, plan: IPlan | null): PlanDetail | null {
    if (!raw) {
      return null;
    }

    if (this.disabledAddonsDealerCodes.length === 0) {
      const disabledAddonsSetting = this.resourceService.getPortalSetting('Global_Disabled_Addons_Dealer_Codes')?.Value;
      if (disabledAddonsSetting) {
        this.disabledAddonsDealerCodes = disabledAddonsSetting.split(',').map((code) => code.trim());
      }
    }

    const equipmentList: Equipment[] = [];

    // 1) PlanType from planCode prefix
    const planType: PlanTier = this.getPlanType(raw.planCode);

    // helpers
    const planCoreConst = this.resourceService.getPortalLabel('Global_Core_Plan_Details_Prefix').Value;
    const planNonCoreConst = this.resourceService.getPortalLabel('Global_NonCore_Plan_Details_Prefix').Value;
    const isCore = (c: Certificate) => c.certificateNumber.startsWith(planCoreConst);
    const isNonCore = (c: Certificate) => c.certificateNumber.startsWith(planNonCoreConst);

    // 2) TotalLiabilityLimit = liabilityLimit from the first CORE-* certificate
    let coreCert = raw.certificates.find(isCore);
    if (!coreCert && this.resourceService.getPortalSetting('Global_Core_Plan_Allow_Default')?.Value === 'true') {
      coreCert = raw.certificates[0];
    }
    const nonCoreCert = raw.certificates.find(isNonCore);
    const totalLiabilityLimit = coreCert?.product?.liabilityLimit ?? 0;

    // 3) DeviceTypeCodes = unique union of:
    //    - all allowedDeviceTypes from CORE-* certificates
    //    - all equipments[].deviceTypeCode across ALL certificates
    const deviceTypeSet = new Set<string>();
    const coverageTypeSet = new Set<string>();
    raw.certificates.filter(isCore).forEach(c => {
      (c.product?.allowedDeviceTypes ?? []).forEach(dt => deviceTypeSet.add(dt));
      // loop thru all coverages and get all codes for coverages field
      c.coverages?.forEach((cov: Coverage) => {
        if (cov.coverageTypeCode == 'CV') {
          if (planType == AppConstants.PLAN.PlanTier.Best ) {
            // do not add 'CV' for premium plans
          } else {
            coverageTypeSet.add(cov.coverageTypeCode);
          }
        } else {
          if (cov.coverageTypeCode) {
            coverageTypeSet.add(cov.coverageTypeCode);
          }
        }
      });
    });

    raw.certificates.forEach((c) => {
      if (c.product?.productDescription?.toLowerCase().startsWith('guest')) {
        deviceTypeSet.add('GUEST');
        return;
      }

      // Add allowedDeviceTypes from product
      const allowedTypes = c.product?.allowedDeviceTypes ?? [];
      allowedTypes.forEach((dt) => deviceTypeSet.add(dt));

      (c.equipments ?? []).forEach((eq) => {
        eq.childCertificateNumber = c.certificateNumber; // link back to child certificate
        const state = plan?.customers?.[0]?.addresses?.[0]?.state?.toLowerCase();
        const hiddenDeviceTypeCodes = state ? this.resourceService.getPortalSetting('Global_Location_Hidden_Equipment_DeviceTypeCode_' + state)?.Value : undefined;
        let hiddenCodesArray: string[] = [];
        if (hiddenDeviceTypeCodes) {
          hiddenCodesArray = hiddenDeviceTypeCodes.split(';').map((code: string) => code.trim());
        }
        if (hiddenCodesArray.includes(eq.deviceTypeCode!)) {
          return; // skip adding this equipment
        }
        equipmentList.push(eq);
        if (eq?.deviceTypeCode) deviceTypeSet.add(eq.deviceTypeCode);
      });
    });

    const deviceTypeCodes = Array.from(deviceTypeSet);
    // MP: This is a hack to add some business rules for coverageTypeCodes
    // if ELEC devicetype is preset and SP for surge protection only if the plan is not part of CA state
    if (deviceTypeCodes.indexOf('ELEC') > -1) {
      if (plan?.customers?.[0]?.addresses?.[0]?.state?.toLowerCase() !== 'california') {
        coverageTypeSet.add('SP');
      }
    }

    const coverageTypeCodes = Array.from(coverageTypeSet).filter((ctc) => {
      //filter by plan type, for example we added 'SP' in the previous step, but we want to remove it for certain dealers
      //we also want to be more flexible so we use portal settings to define the dealer codes to filter out
      const filter = this.resourceService.getPortalSetting(`Global_Plan_Details_Remove_${ctc}_By_Plan_Type`)?.Value;
      if (filter) {
        const dealerCodes = filter.split(',').map((code) => code.trim());
        if (dealerCodes.includes(planType)) {
          return false;
        }
      }
      return true;
    });

    // 4) Payment history
    //    - First item: { date: planSalesDate, amount: premium.gross from CORE, planType: 'Plan' }
    //    - Then: group NON-CORE certificates by certificateStartDate,
    //            sum their salesPrice for that date, each becomes an item with planType 'Add-Ons'
    const paymentHistory: PaymentHistoryItem[] = [
      {
        date: raw.planSalesDate,
        amount: raw.certificates.filter(isCore)[0]?.premium?.gross ?? 0,
        planType: 'Plan',
      },
    ];

    const addonGroups = new Map<string, number>();
    raw.certificates
      .filter(c => !isCore(c))
      .forEach(c => {
        const key = c.certificateSalesDate;
        addonGroups.set(key, (addonGroups.get(key) ?? 0) + (c.premium?.gross ?? 0));
      });

    // stable order by date ascending (optional, but nice)
    Array.from(addonGroups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([date, amount]) => {
        paymentHistory.push({ date, amount, planType: 'Add-Ons' });
      });

    let coverageMB = coreCert?.coverages?.find((cov: Coverage) => cov.coverageTypeCode === 'MB');
    if (nonCoreCert && !coverageMB?.deductibles?.find(d => !!d?.deductible)) {
      coverageMB = nonCoreCert?.coverages?.find((cov: Coverage) => cov.coverageTypeCode === 'MB');
    }

    //from the coverageMB, get the deductible amount if it exists
    const deductible = coverageMB?.deductibles?.find(d =>
      d.deductibleBasedOnCode === AppConstants.PLAN.Deductible.Fixed &&
      d.methodOfRepairCode === AppConstants.PLAN.Deductible.MethodOfRepairCodeH &&
      AppConstants.PLAN.Deductible.Source?.split('|')?.includes(d.deductibleSource)
    )?.deductible ?? 0;

    // sort by registeredItemName (optional, but nice)
    equipmentList.sort((a, b) =>
      a.registeredItemName.localeCompare(b.registeredItemName, undefined, {
        sensitivity: 'base',
        numeric: true,
      })
    );

    // Return enriched object (non-mutating)
    const result = {
      ...raw,
      planType,
      totalLiabilityLimit,
      deviceTypeCodes,
      coverageTypeCodes,
      paymentHistory,
      deductible,
      coreCertificate: coreCert?.certificateNumber ?? '',
      nonCoreCertificate: nonCoreCert?.certificateNumber ?? '',
      equipments: equipmentList,
      isAddonsAllowed: this.disabledAddonsDealerCodes.length === 0 || !this.disabledAddonsDealerCodes.includes(raw.dealerCode),
      planStartDate: coverageMB?.beginDate ?? raw.planSalesDate,
      planEndDate: coverageMB?.endDate ?? add(new Date(raw.planSalesDate), { months: raw.planTerm }).toISOString(),
    };

    return result;
  }

  getPlanType(planCode: string) {
    if (!this.goodPlanTierTag || !this.betterPlanTierTag || !this.bestPlanTierTag) {
      this.goodPlanTierTag = this.resourceService.getPortalSetting('Global_Plan_Prefix_Good')?.Value;
      this.betterPlanTierTag = this.resourceService.getPortalSetting('Global_Plan_Prefix_Better')?.Value;
      this.bestPlanTierTag = this.resourceService.getPortalSetting('Global_Plan_Prefix_Best')?.Value;
      this.listingPlanTierTag = this.resourceService.getPortalSetting('Global_Plan_Prefix_Listing')?.Value;
    }

    if (!planCode) {
      return AppConstants.PLAN.PlanTier.Good as PlanTier;
    }

    return planCode.indexOf(this.goodPlanTierTag)>-1 ? AppConstants.PLAN.PlanTier.Good as PlanTier
      : planCode.indexOf(this.betterPlanTierTag)>-1 ? AppConstants.PLAN.PlanTier.Better as PlanTier :
        planCode.indexOf(this.bestPlanTierTag)>-1 ? AppConstants.PLAN.PlanTier.Best as PlanTier :
        planCode.indexOf(this.listingPlanTierTag)>-1 ? AppConstants.PLAN.PlanTier.Listing as PlanTier :
          AppConstants.PLAN.PlanTier.Good as PlanTier;
  }

  getTypeKey(claim: Claim, type: ClaimStatusKeys): string {
    const prefix = claim?.fulfillment?.entityType && claim?.fulfillment?.typeCode
      ? this.getPrefixKey(claim) : this.getDefaultPrefixKey(claim);
    return `${prefix}_${type}`;
  }

  getPrefixKey(claim?: Claim): string {
    if (claim) {
      let isActionPresent = claim?.actionsSummary?.actionsCount > 0;
      let entityType = claim?.fulfillment?.entityType;
      let typeCode = claim?.fulfillment?.typeCode;

      let baseKey = this.getClaimTrackDetailsKeyPrefix(claim?.fulfillment?.fulfillmentTypeEnum, claim?.status, isActionPresent, claim?.lossInfo?.coverageTypeCode);

      return `${baseKey}_${entityType}_${typeCode}`;
    }
    return '';
  }

  getDefaultTypeKey(claim: Claim, type: ClaimStatusKeys): string {
    const prefix = this.getDefaultPrefixKey(claim);
    return `${prefix}_${type}`;
  }

  getDefaultPrefixKey(claim: Claim): string {
    const status = claim?.status ?? '';
    const isActionPresent = claim?.actionsSummary?.actionsCount > 0;

    if (status?.toLowerCase() === 'pending') {
      return `${status}_${isActionPresent ? 'T' : 'F'}`;
    }
    return status;
  }

  getClaimTrackDetailsKeyPrefix(
    fulfillmentTypeEnum: string,
    status: string,
    isActionPresent: boolean,
    coverageTypeCode: string
  ): string {
    return `${fulfillmentTypeEnum}_${status}_${isActionPresent ? 'T' : 'F'}_${coverageTypeCode}`;
  }
}
